/**
 * <gff-hero-slider> — Good Family Farms hero background fade slider.
 *
 * Cross-fades up to 4 background image slides (opacity transition, not
 * translate). Each slide continues the Ken Burns zoom animation on the
 * active slide's image via the CSS `.is-active` class toggle.
 *
 * Markup contract (Liquid emits this):
 *   <gff-hero-slider class="gff-hero__slides" data-interval="4000">
 *     <div class="gff-hero__slide is-active" data-slide-index="0">
 *       <img class="gff-hero__img" loading="eager" fetchpriority="high" …>
 *     </div>
 *     <div class="gff-hero__slide" data-slide-index="1">
 *       <img class="gff-hero__img" loading="lazy" …>
 *     </div>
 *     …
 *   </gff-hero-slider>
 *
 * Behaviour:
 *   - ≤1 slide  → no timer started (single static image, no-op).
 *   - prefers-reduced-motion → no timer started (slide 1 stays visible).
 *   - document.hidden → timer paused; resumes on visibility restored.
 *   - disconnectedCallback → clears timer + removes visibilitychange listener.
 *   - No ARIA live region: slides are decorative background imagery.
 */
class GffHeroSlider extends HTMLElement {
  #timer = null;
  #current = 0;
  #slides = [];
  #interval = 4000;
  #onVisibility = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.gff-hero__slide'));

    // Fewer than 2 slides — nothing to do.
    if (this.#slides.length < 2) return;

    // Respect prefers-reduced-motion — no crossfade, no Ken Burns cycling.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Read interval from data attribute (ms), fall back to 4000.
    const raw = parseInt(this.dataset.interval, 10);
    this.#interval = Number.isFinite(raw) && raw > 0 ? raw : 4000;

    // Ensure slide 0 starts as active (CSS no-JS fallback sets this, but
    // guard in case JS replaces the DOM after the fact).
    this.#current = 0;
    this.#activateSlide(0);

    // Pause / resume on tab visibility.
    this.#onVisibility = () => {
      if (document.hidden) {
        this.#stopTimer();
      } else {
        this.#startTimer();
      }
    };
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Start unless already hidden.
    if (!document.hidden) {
      this.#startTimer();
    }
  }

  disconnectedCallback() {
    this.#stopTimer();
    if (this.#onVisibility) {
      document.removeEventListener('visibilitychange', this.#onVisibility);
      this.#onVisibility = null;
    }
  }

  #startTimer() {
    if (this.#timer !== null) return;
    this.#timer = setInterval(() => this.#advance(), this.#interval);
  }

  #stopTimer() {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #advance() {
    const next = (this.#current + 1) % this.#slides.length;
    this.#activateSlide(next);
    this.#current = next;
  }

  #activateSlide(index) {
    for (let i = 0; i < this.#slides.length; i++) {
      const slide = this.#slides[i];
      const active = i === index;
      slide.classList.toggle('is-active', active);
      // Inactive slides are decorative; hide from AT when not displayed.
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    }
  }
}

if (!customElements.get('gff-hero-slider')) {
  customElements.define('gff-hero-slider', GffHeroSlider);
}
